import { useEffect, useState } from 'react';
import { fetchOnboardings, type OnboardingRow } from '../lib/api.js';

export function AdminBootstrapPage() {
  const initialKey = readKeyFromUrl();
  const [keyInput, setKeyInput] = useState(initialKey);
  const [rows, setRows] = useState<OnboardingRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!keyInput) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    fetchOnboardings(keyInput)
      .then((res) => {
        if (!cancelled) setRows(res.onboardings);
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'Failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [keyInput]);

  return (
    <main className="max-w-5xl mx-auto px-6 py-12">
      <h1 className="text-2xl font-semibold text-brand-primary mb-2">Admin (M1 bootstrap)</h1>
      <p className="text-brand-muted text-sm mb-6">
        Read-only view of onboarding submissions. Bootstrap-key-gated; replaced by full BetterAuth
        admin auth in M2.
      </p>

      {!keyInput && <KeyEntry onSet={setKeyInput} />}

      {keyInput && err && (
        <div className="mb-4">
          <p className="text-red-700 bg-red-50 border border-red-200 rounded p-3 mb-3">{err}</p>
          <button
            type="button"
            onClick={() => {
              writeKeyToUrl('');
              setKeyInput('');
              setRows(null);
              setErr(null);
            }}
            className="text-sm text-brand-primary underline"
          >
            Re-enter bootstrap key
          </button>
        </div>
      )}

      {loading && <p className="text-brand-muted">Loading...</p>}

      {rows && (
        <>
          <p className="text-sm text-brand-muted mb-3">
            {rows.length} submission{rows.length === 1 ? '' : 's'} (newest first, capped at 100)
          </p>
          <div className="overflow-x-auto bg-white rounded-md border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-brand-bg">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Submitted</th>
                  <th className="px-4 py-2 text-left font-medium">Business</th>
                  <th className="px-4 py-2 text-left font-medium">Owner</th>
                  <th className="px-4 py-2 text-left font-medium">Contact</th>
                  <th className="px-4 py-2 text-left font-medium">Type</th>
                  <th className="px-4 py-2 text-left font-medium">Volume</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 align-top">
                    <td className="px-4 py-2 whitespace-nowrap">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2">{r.legalName}</td>
                    <td className="px-4 py-2">{r.ownerName}</td>
                    <td className="px-4 py-2">
                      <div>{r.ownerEmail}</div>
                      <div className="text-brand-muted">{r.ownerPhone}</div>
                    </td>
                    <td className="px-4 py-2">{r.businessType}</td>
                    <td className="px-4 py-2 whitespace-nowrap">{r.monthlyVolumeEstimate}</td>
                    <td className="px-4 py-2">{r.status}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-brand-muted">
                      No onboarding submissions yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function KeyEntry({ onSet }: { onSet: (key: string) => void }) {
  const [val, setVal] = useState('');
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!val) return;
        writeKeyToUrl(val);
        onSet(val);
      }}
      className="mb-6 max-w-md"
    >
      <label className="block text-sm font-medium mb-1" htmlFor="bootstrap-key">
        Bootstrap key
      </label>
      <input
        id="bootstrap-key"
        type="password"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="input"
        autoComplete="off"
      />
      <button
        type="submit"
        className="mt-2 bg-brand-primary hover:brightness-95 text-white px-4 py-2 rounded-md font-medium"
      >
        Load
      </button>
    </form>
  );
}

function readKeyFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('bootstrap_key') ?? '';
}

function writeKeyToUrl(key: string): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (key) {
    params.set('bootstrap_key', key);
  } else {
    params.delete('bootstrap_key');
  }
  const qs = params.toString();
  window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
}
